import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import type { CrmTeamResponse, CrmTeamPage } from "../../../types/crm";
import { getCrmTeams } from "../../../services/crmTeams";
import { DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from "../../../constants/extraction";
import { CreateCrmTeamModal } from "./CreateCrmTeamModal";
import { SchoolsBulkOutreachModal } from "./SchoolsBulkOutreachModal";
import { MultiSelectPopover } from "../../../components/MultiSelectPopover";
import { TablePagination } from "../../../components/TablePagination";
import { US_STATES } from "../../../constants/usStates";
import { DIVISIONS } from "../../../constants/divisions";
import {
  loadCrmViewState,
  saveCrmViewState,
  teamsFilterKey,
  teamsScrollKey,
} from "../utils/crmViewState";
import { useCrmScrollRestoration } from "../hooks/useCrmScrollRestoration";
import "./TeamsListPage.css";

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const FILTER_PARAM_KEYS = ["search", "divisions", "states", "page"];

const parseList = (val: string | null): string[] =>
  val ? val.split(",").filter(Boolean) : [];

const readFilterState = (params: URLSearchParams) => ({
  search: params.get("search") || "",
  divisions: parseList(params.get("divisions")),
  states: parseList(params.get("states")),
  currentPage: parseInt(params.get("page") || "1", 10),
});

const hasAnyFilterParam = (params: URLSearchParams): boolean =>
  FILTER_PARAM_KEYS.some((key) => params.has(key));

const getInitialFilterState = (searchParams: URLSearchParams) => {
  if (hasAnyFilterParam(searchParams)) {
    return readFilterState(searchParams);
  }
  const stored = loadCrmViewState(teamsFilterKey());
  return stored ? readFilterState(stored) : readFilterState(new URLSearchParams());
};

export const TeamsListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initialState = getInitialFilterState(searchParams);

  const [teams, setTeams] = useState<CrmTeamResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);

  const [search, setSearch] = useState(initialState.search);
  const [debouncedSearch, setDebouncedSearch] = useState(initialState.search);
  const [divisions, setDivisions] = useState<string[]>(initialState.divisions);
  const [states, setStates] = useState<string[]>(initialState.states);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showOutreachModal, setShowOutreachModal] = useState(false);

  const pageCache = useRef<Map<string, CrmTeamPage>>(new Map());
  const clearCache = () => { pageCache.current.clear(); };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== debouncedSearch) {
        setDebouncedSearch(search);
        setCurrentPage(1);
        setSelectedIds(new Set());
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, debouncedSearch]);

  // Sync state to URL params (and mirror into sessionStorage for the tab-long persistence).
  // Runs on first mount too so that filters hydrated from sessionStorage are reflected in the URL.
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (divisions.length > 0) params.set("divisions", divisions.join(","));
    if (states.length > 0) params.set("states", states.join(","));
    if (currentPage > 1) params.set("page", currentPage.toString());

    setSearchParams(params, { replace: true });
    saveCrmViewState(teamsFilterKey(), params);
  }, [debouncedSearch, divisions, states, currentPage, setSearchParams]);

  const handleDivisionsChange = (val: string[]) => {
    setDivisions(val);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handleStatesChange = (val: string[]) => {
    setStates(val);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const fetchTeams = useCallback(async (skipCache = false) => {
    const params = {
      search: debouncedSearch || undefined,
      divisions: divisions.length > 0 ? divisions : undefined,
      states: states.length > 0 ? states : undefined,
      pageNumber: currentPage,
      pageSize: DEFAULT_PAGE_SIZE,
    };
    const cacheKey = JSON.stringify(params);

    if (!skipCache) {
      const cached = pageCache.current.get(cacheKey);
      if (cached) {
        setTeams(cached.items);
        setTotalCount(cached.totalCount);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    try {
      const data = await getCrmTeams(params);
      pageCache.current.set(cacheKey, data);
      setTeams(data.items);
      setTotalCount(data.totalCount);
    } catch (err) {
      console.error("Failed to load CRM teams:", err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, divisions, states, currentPage]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  useCrmScrollRestoration(teamsScrollKey(), !loading && teams.length > 0);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSelectedIds(new Set());
  };

  const allSelected = teams.length > 0 && teams.every((t) => selectedIds.has(t.id));
  const someSelected = teams.some((t) => selectedIds.has(t.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        teams.forEach((t) => next.delete(t.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        teams.forEach((t) => next.add(t.id));
        return next;
      });
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedSchoolIds = Array.from(selectedIds);

  return (
    <div className="teams-list-page">
      <div className="teams-list-page__header">
        <h1 className="teams-list-page__title">Schools</h1>
        {totalCount > 0 && (
          <span className="teams-list-page__count">{totalCount.toLocaleString()}</span>
        )}
      </div>

      <div className="teams-list-page__toolbar">
        <div className="teams-list-page__filters">
          <input
            className="teams-list-page__search"
            type="text"
            placeholder="Search schools..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="teams-list-page__filter-division">
            <MultiSelectPopover
              options={DIVISIONS}
              value={divisions}
              onChange={handleDivisionsChange}
              placeholder="All Divisions"
            />
          </div>
          <div className="teams-list-page__filter-state">
            <MultiSelectPopover
              options={US_STATES}
              value={states}
              onChange={handleStatesChange}
              placeholder="State"
              searchable
            />
          </div>
        </div>
        <div className="teams-list-page__actions">
          <button
            className="teams-list-page__outreach-btn"
            onClick={() => setShowOutreachModal(true)}
            disabled={selectedSchoolIds.length === 0}
          >
            Send Bulk Outreach{selectedSchoolIds.length > 0 ? ` (${selectedSchoolIds.length})` : ""}
          </button>
          <button
            className="teams-list-page__new-btn"
            onClick={() => setShowCreateModal(true)}
          >
            + New School
          </button>
        </div>
      </div>

      <div className="teams-list-page__table-wrapper">
        {loading ? (
          <div className="teams-list-page__loading">
            <div className="teams-list-page__spinner" />
          </div>
        ) : teams.length === 0 ? (
          <div className="teams-list-page__empty">
            {debouncedSearch || divisions.length > 0 || states.length > 0
              ? "No schools match your filters."
              : "No schools found."}
          </div>
        ) : (
          <div className="teams-table-container">
            <table className="teams-table">
              <thead className="teams-table__header">
                <tr>
                  <th className="teams-table__th teams-table__th--checkbox">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                      onChange={toggleSelectAll}
                      aria-label="Select all schools"
                    />
                  </th>
                  <th className="teams-table__th">Name</th>
                  <th className="teams-table__th">Division</th>
                  <th className="teams-table__th">State</th>
                  <th className="teams-table__th">Coaches</th>
                  <th className="teams-table__th teams-table__th--date">Created</th>
                </tr>
              </thead>
              <tbody>
                {teams.map((team) => (
                  <tr
                    key={team.id}
                    className={`teams-table__row teams-table__row--clickable${selectedIds.has(team.id) ? " teams-table__row--selected" : ""}`}
                    onClick={() =>
                      navigate(`/crm/teams/${team.id}`, {
                        state: { from: `${location.pathname}${location.search}` },
                      })
                    }
                  >
                    <td
                      className="teams-table__td teams-table__td--checkbox"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(team.id)}
                        onChange={() => toggleSelect(team.id)}
                        aria-label={`Select ${team.name}`}
                      />
                    </td>
                    <td className="teams-table__td teams-table__td--name">{team.name}</td>
                    <td className="teams-table__td">
                      {team.division ?? <span className="teams-table__empty-cell">—</span>}
                    </td>
                    <td className="teams-table__td">
                      {team.state ?? <span className="teams-table__empty-cell">—</span>}
                    </td>
                    <td className="teams-table__td">{team.coachCount}</td>
                    <td className="teams-table__td teams-table__td--date">{formatDate(team.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="teams-list-page__pagination">
        <TablePagination
          currentPage={currentPage}
          pageSize={DEFAULT_PAGE_SIZE}
          totalCount={totalCount}
          onPageChange={handlePageChange}
        />
      </div>

      <CreateCrmTeamModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          clearCache();
          fetchTeams(true);
        }}
      />

      <SchoolsBulkOutreachModal
        isOpen={showOutreachModal}
        schoolIds={selectedSchoolIds}
        onClose={() => setShowOutreachModal(false)}
        onDone={() => {
          setShowOutreachModal(false);
          setSelectedIds(new Set());
        }}
      />
    </div>
  );
};
